import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { TopClient } from '../hooks/useOverviewData';
import { formatCurrency } from '../utils/format';
import { downloadCsv, todayYmd } from '../utils/csv';
import LeaderboardCard, { LeaderboardDelta, LeaderboardItem } from './LeaderboardCard';

interface Props {
    clients: TopClient[] | null;
    // Previous-period revenue for the same clients — used to render delta
    // pills (↑ N% / ↓ N% / NEW). Optional because not every call site
    // currently has period-over-period data wired (overview page passes only
    // current; the analytics deep-dive page passes both). When undefined or
    // explicitly null, delta pills are omitted entirely.
    clientsPrevious?: TopClient[] | null;
    loading: boolean;
    // Page passes 3 on mobile to keep this card short; "Show all" still reveals up to 10.
    defaultRows?: number;
}

// Module-scope so it can be referenced from the useMemo callback below
// without hitting TS2304 / temporal-dead-zone. Pure utility; intentionally
// not exported — local enough that LeaderboardCard's own % formatter would
// also work if needed.
const formatPct = (pct: number, locale: string): string => {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
        minimumFractionDigits: 0,
    }).format(pct) + '%';
};

const TopClientsCard: React.FC<Props> = ({ clients, clientsPrevious, loading, defaultRows }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const previousById = useMemo(() => {
        const map = new Map<number, TopClient>();
        (clientsPrevious ?? []).forEach((c) => map.set(c.client_id, c));
        return map;
    }, [clientsPrevious]);

    const items: LeaderboardItem[] | null = useMemo(() => {
        if (!clients) return null;
        return clients.map((c) => {
            const prev = previousById.get(c.client_id);
            let delta: LeaderboardDelta | undefined;
            if (clientsPrevious != null) {
                if (!prev || prev.revenue <= 0) {
                    delta = {
                        severity: 'new',
                        label: (
                            <FormattedMessage
                                id="analyticsV2.leaderboard.deltaNew"
                                defaultMessage="NEW"
                            />
                        ),
                    };
                } else {
                    const pct = ((c.revenue - prev.revenue) / prev.revenue) * 100;
                    const rounded = Math.round(pct * 10) / 10;
                    if (Math.abs(rounded) < 0.1) {
                        delta = { severity: 'neutral', label: '—' };
                    } else if (rounded > 0) {
                        delta = { severity: 'positive', label: `↑ ${formatPct(rounded, locale)}` };
                    } else {
                        delta = { severity: 'negative', label: `↓ ${formatPct(Math.abs(rounded), locale)}` };
                    }
                }
            }
            return {
                id: c.client_id,
                primary: c.name,
                secondary: c.city || null,
                value: c.revenue,
                valueDisplay: formatCurrency(c.revenue, locale),
                meta: (
                    <FormattedMessage
                        id="analyticsV2.topClients.agreementCount"
                        defaultMessage="{count, plural, one {# agreement} other {# agreements}}"
                        values={{ count: c.agreement_count }}
                    />
                ),
                delta,
            };
        });
    }, [clients, clientsPrevious, previousById, locale]);

    const handleExport = () => {
        if (!clients || clients.length === 0) return;
        downloadCsv<TopClient & { rank: number }>(
            `top-clients-${todayYmd()}.csv`,
            [
                { header: 'Rank', accessor: (r) => r.rank },
                { header: 'Client', accessor: (r) => r.name },
                { header: 'City', accessor: (r) => r.city ?? '' },
                { header: 'Revenue', accessor: (r) => r.revenue },
                { header: 'Agreements', accessor: (r) => r.agreement_count },
            ],
            clients.map((c, i) => ({ ...c, rank: i + 1 })),
        );
    };

    return (
        <LeaderboardCard
            title={<FormattedMessage id="analyticsV2.topClients.title" defaultMessage="Top clients" />}
            subtitle={
                <FormattedMessage
                    id="analyticsV2.topClients.subtitle"
                    defaultMessage="By revenue in the current period"
                />
            }
            items={items}
            loading={loading}
            accent="primary"
            emptyMessage={
                <FormattedMessage
                    id="analyticsV2.topClients.empty"
                    defaultMessage="No client revenue in this period yet."
                />
            }
            onExport={handleExport}
            defaultRows={defaultRows}
        />
    );
};

export default TopClientsCard;
